import {
	Box,
	Button,
	Card,
	Collapse,
	Group,
	UnstyledButton
} from '@mantine/core';
import { useDisclosure, useResizeObserver, useTimeout } from '@mantine/hooks';
import { type Node, type NodeProps, Position } from '@xyflow/react';
import clsx from 'clsx';
import { type MotionStyle, motion } from 'framer-motion';
import { GripVertical } from 'lucide-react';
import {
	type FC,
	type MouseEvent,
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import Markdown from 'react-markdown';
import { useShallow } from 'zustand/react/shallow';
import config from '@/app.config';
import FlowCardActions from '@/components/flow-card/components/flow-card-actions';
import FlowCardHandle from '@/components/flow-card/components/flow-card-handle';
import { FlowCardViewMode } from '@/components/flow-card/components/flow-card-view-mode';
import styles from '@/components/flow-card/flow-card.module.css';
import { useCardDrag } from '@/hooks/use-card-drag';
import useEmitter from '@/hooks/use-emitter.ts';
import { useAutoMiddleEllipsis } from '@/hooks/use-middleTruncate';
import useFlowStore from '@/store/flow';
import { TCardSize, type TFlowItemFrontend } from '@/types/common';
import { AppEvent, type EventPayloads } from '@/types/event-emitter.types.ts';
import { editorValueToMarkdown } from '@/utils/editor-conversion';
import { emitter } from '@/utils/event-emitter.ts';
import {
	needsReadMoreButton,
	truncateContentForReadMore
} from '@/utils/get-flow-card-height.ts';

type FlowCardNode = Node<TFlowItemFrontend, 'flowCard'>;
type FlowCardStyle = MotionStyle & Record<'--type-color', string>;

const FlowCard: FC<NodeProps<FlowCardNode>> = memo(({ data }) => {
	const [wrapperRef, wrapperRect] = useResizeObserver();
	const [headerRef, headerRect] = useResizeObserver();
	const [contentRef] = useResizeObserver();
	const [isDescriptionView, { toggle, open: setDescriptionView }] =
		useDisclosure(true);

	const isReadMoreNeeded = useMemo(() => {
		const collapsedMap = { [data.source.idn]: data.isCollapsed };
		const changedViewCards = { [data.source.idn]: !isDescriptionView };
		return needsReadMoreButton(data, collapsedMap, changedViewCards);
	}, [data, isDescriptionView]);

	const [isShaking, setIsShaking] = useState(false);
	const { start: startShakeTimeout } = useTimeout(
		() => setIsShaking(false),
		1000
	);

	const { isDragging, dragY, onDragHandlePointerDown } = useCardDrag();

	const { openFlowDrawer, canvas, dragState } = useFlowStore(
		useShallow((state) => ({
			openFlowDrawer: state.openFlowDrawer,
			canvas: state.canvas,
			dragState: state.dragState
		}))
	);

	const { columnId, cardIndex } = useMemo(() => {
		const nodeId = data.source.idn;
		for (const typeIdn in canvas.sections) {
			const items = canvas.sections[typeIdn];
			const idx = items.findIndex((item) => item.idn === nodeId);
			if (idx !== -1) {
				return { columnId: typeIdn, cardIndex: idx };
			}
		}
		return { columnId: '', cardIndex: -1 };
	}, [canvas.sections, data.source.idn]);

	const isCurrentDropTarget =
		!isDragging &&
		dragState?.columnId === columnId &&
		dragState?.toIndex === cardIndex;

	const isMovingUp =
		dragState != null && dragState.toIndex < dragState.fromIndex;

	const isMovingDown =
		dragState != null && dragState.toIndex > dragState.fromIndex;

	const showIndicatorAbove = isCurrentDropTarget && isMovingUp;
	const showIndicatorBelow = isCurrentDropTarget && isMovingDown;

	const handleEdit = (ev: MouseEvent<HTMLButtonElement>) => {
		ev.stopPropagation();
		openFlowDrawer(data);
	};

	const contentHeight = useMemo(() => {
		if (!data.isCollapsed || !wrapperRect || !headerRect) {
			return data.cardHeight;
		}

		return wrapperRect.height - headerRect.height;
	}, [data.isCollapsed, data.cardHeight, wrapperRect, headerRect]);

	const handleToggleCard = useCallback(
		(ev: MouseEvent<HTMLElement>) => {
			ev.stopPropagation();
			if (isDragging) {
				return;
			}
			emitter.emit(AppEvent.COLLAPSE_EXPAND_CARD, {
				cardId: data.source.idn,
				isCollapsed: !data.isCollapsed
			});
			setDescriptionView();
		},
		[isDragging, data.source.idn, data.isCollapsed, setDescriptionView]
	);

	const handleShakeCard = useCallback(
		(payload: EventPayloads[AppEvent.ANIMATE_CLONED_FLOW_CARD]) => {
			if (payload.cardId !== data.source.idn) {
				return;
			}
			setIsShaking(true);
			startShakeTimeout();
		},
		[data.source.idn, startShakeTimeout]
	);

	const handleToggleDescriptionView = () => {
		emitter.emit(AppEvent.CARD_VIEW_CHANGED, {
			isDescriptionView: !isDescriptionView,
			cardId: data.source.idn
		});
		toggle();
	};

	useEmitter(AppEvent.ANIMATE_CLONED_FLOW_CARD, handleShakeCard);

	const titleMarkdown = useMemo(
		() => editorValueToMarkdown(data.source.title),
		[data.source.title]
	);

	const bodyMarkdown = useMemo(
		() => editorValueToMarkdown(data.source.body),
		[data.source.body]
	);

	const displayContent = useMemo(() => {
		const fullContent = isDescriptionView
			? data.source.description
			: bodyMarkdown;

		if (!isReadMoreNeeded || !fullContent) {
			return fullContent;
		}

		const bodyViewHeight = config.view.extended.flowCard.bodyViewHeight;
		const extendedHeight = config.view.extended.flowCard.height;
		const maxAllowedHeight = isDescriptionView
			? extendedHeight
			: bodyViewHeight;

		const headerPadding = 13 * 2;
		const headerContentHeight = 20;
		const headerHeight = headerPadding + headerContentHeight;

		const contentPaddingTop = 40;
		const contentPaddingBottom = 20;
		const readMoreButtonHeight = 24;
		const readMoreSpacing = 16;
		const extraBottomSpacing = 8;

		const availableContentHeight =
			maxAllowedHeight -
			headerHeight -
			contentPaddingTop -
			contentPaddingBottom -
			readMoreSpacing -
			readMoreButtonHeight -
			extraBottomSpacing;
		const contentWidth = config.view.extended.flowCard.width - 48;

		return truncateContentForReadMore(
			fullContent,
			availableContentHeight,
			!isDescriptionView,
			contentWidth
		);
	}, [
		isReadMoreNeeded,
		isDescriptionView,
		data.source.description,
		bodyMarkdown
	]);

	const { ref, text } = useAutoMiddleEllipsis(titleMarkdown);
	return (
		<motion.div
			className={clsx('nopan', styles.wrapper, {
				[styles.opened]: data.isCollapsed,
				[styles.shake]: isShaking,
				[styles.dragging]: isDragging
			})}
			data-dragging={isDragging || undefined}
			ref={wrapperRef}
			style={
				{
					y: dragY,
					'--type-color': data.color,
					height: data.cardHeight
				} as FlowCardStyle
			}
		>
			{showIndicatorAbove && (
				<div
					className={clsx(styles.dropIndicator, styles.dropIndicatorAbove)}
				/>
			)}
			<Card
				className={clsx(styles.card, {
					[styles.opened]: data.isCollapsed,
					[styles.disabledCard]: data.source.is_from_library
				})}
				h={data.cardHeight}
				radius="md"
				shadow="sm"
				w={
					config.view[data.isCollapsed ? TCardSize.EXTENDED : TCardSize.COMPACT]
						.flowCard.width
				}
			>
				<UnstyledButton
					className={styles.heading}
					classNames={{
						root: styles.headingRoot
					}}
					onClick={handleToggleCard}
					ref={headerRef}
				>
					<Group
						justify="space-between"
						wrap="nowrap"
					>
						<button
							className={styles.dragHandle}
							onClick={(e) => e.stopPropagation()}
							onPointerDown={onDragHandlePointerDown}
							type="button"
						>
							<GripVertical size={25} />
						</button>

						<Box
							className={styles.title}
							ref={ref}
						>
							<Markdown>{text}</Markdown>
						</Box>

						<FlowCardActions
							color={data.color}
							isFromLibrary={data.source.is_from_library}
							onEdit={handleEdit}
						/>
					</Group>
				</UnstyledButton>

				<Collapse
					in={data.isCollapsed}
					keepMounted
					transitionDuration={0.8}
				>
					<Box ref={contentRef}>
						<FlowCardViewMode
							color={data.color}
							content={displayContent}
							contentHeight={contentHeight}
							handleToggleDescriptionView={handleToggleDescriptionView}
							isDescriptionView={isDescriptionView}
							isTruncated={isReadMoreNeeded}
						/>
					</Box>
					{isReadMoreNeeded && (
						<Button
							className={styles.readMoreButton}
							onClick={handleEdit}
						>
							Read More
						</Button>
					)}
				</Collapse>
			</Card>

			{showIndicatorBelow && (
				<div
					className={clsx(styles.dropIndicator, styles.dropIndicatorBelow)}
				/>
			)}

			<div className={styles.sourceHandles}>
				{data.outgoingMentions.map((mention) => (
					<FlowCardHandle
						id={`${data.source.idn}-${mention.item_idn}`}
						key={`${data.source.idn}-${mention.item_idn}`}
						position={Position.Right}
						type="source"
					/>
				))}
			</div>

			<div className={styles.targetHandles}>
				{data.incomingMentions.map((mention) => (
					<FlowCardHandle
						id={`${data.source.idn}-${mention.item_idn}`}
						key={`${data.source.idn}-${mention.item_idn}`}
						position={Position.Left}
						type="target"
					/>
				))}
			</div>
		</motion.div>
	);
});

export default FlowCard;
